/**
 * Doctor dashboard overview: today's numbers, monthly comparison, daily heat map.
 *
 * Replaces `renderDoctorDashboard_v2` and `renderMonthlyStats` — about 500 lines
 * of template-literal HTML, plus the `switchDoctorTab` router that swapped
 * `innerHTML` on a single content div.
 *
 * The figures come from GET /api/doctors/dashboard, which already computes them
 * server-side; `domain/stats` only reshapes the response.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  PageHeader,
  Panel,
  SectionTitle,
  Select,
  Spinner,
  StatCard,
} from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useAsync } from '../../hooks/useAsync';
import { useDoctorBookings } from '../../hooks/useBookings';
import { doctorApi } from '../../api/endpoints';
import { formatCurrency } from '../../domain/pricing';
import {
  DAY_STATUS_LABELS,
  DAY_STATUS_STYLES,
  compareMonths,
  normalizeDashboard,
} from '../../domain/stats';
import { statusLabel, statusStyle, typeLabel } from '../../domain/bookings';
import { formatTime12, todayKey, isoToDisplayDate } from '../../domain/dates';

const MONTH_OPTIONS = [
  { value: 'currentMonth', label: 'الشهر الحالي' },
  { value: 'lastMonth', label: 'الشهر الماضي' },
  { value: 'lastTwoMonths', label: 'قبل شهرين' },
];

export function DoctorOverview() {
  const { doctorId, doctorProfile, session } = useAuth();

  const dashboardState = useAsync(() => doctorApi.dashboard(), [doctorId], {
    enabled: Boolean(doctorId),
  });

  const today = todayKey();
  const { bookings: todayBookings, loading: bookingsLoading } = useDoctorBookings(doctorId, {
    dateKey: today,
  });

  const [selectedMonth, setSelectedMonth] = useState('currentMonth');

  const dashboard = useMemo(() => normalizeDashboard(dashboardState.data), [dashboardState.data]);
  const overview = dashboard.todayOverview;

  const comparison = useMemo(() => {
    const stats = dashboard.comparativeStats;
    if (!stats) return null;
    const current = stats[selectedMonth];
    const previous =
      selectedMonth === 'currentMonth'
        ? stats.lastMonth
        : selectedMonth === 'lastMonth'
          ? stats.lastTwoMonths
          : null;
    return { current, delta: compareMonths(current, previous) };
  }, [dashboard.comparativeStats, selectedMonth]);

  const activeDays = useMemo(
    () => dashboard.dailyPerformance.filter((day) => day.totalBookings > 0),
    [dashboard.dailyPerformance],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={`مرحباً، ${doctorProfile?.name || session?.name || 'دكتور'} 👋`}
        subtitle="نظرة عامة على أدائك اليوم"
        icon="layout-dashboard"
        actions={
          <Button
            variant="ghost"
            icon="refresh-cw"
            onClick={dashboardState.reload}
            loading={dashboardState.loading}
          >
            تحديث
          </Button>
        }
      />

      {dashboardState.error ? (
        <ErrorState error={dashboardState.error} onRetry={dashboardState.reload} />
      ) : (
        <>
          {/* Headline numbers */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon="calendar-check-2"
              caption="اليوم"
              label="إجمالي حجوزات اليوم"
              value={overview.totalBookings}
              accent="cyan"
            />
            <StatCard
              icon="clock"
              caption="قيد الانتظار"
              label="حجوزات قادمة"
              value={overview.pendingBookings}
              accent="amber"
            />
            <StatCard
              icon="check-circle-2"
              caption="الشهر الحالي"
              label="كشوفات مكتملة"
              value={overview.completedCheckupsMonth}
              accent="emerald"
            />
            <StatCard
              icon="banknote"
              caption="الإيرادات"
              label="أرباح الشهر الحالي"
              value={formatCurrency(overview.revenueMonth)}
              accent="purple"
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Today's queue */}
            <Panel>
              <div className="mb-4 flex items-center justify-between">
                <SectionTitle icon="list-checks" className="!mb-0">
                  طابور اليوم
                </SectionTitle>
                <Link
                  to="/doctor/schedule"
                  className="text-xs font-bold text-cyan-400 hover:text-cyan-300"
                >
                  إدارة المواعيد
                </Link>
              </div>

              {bookingsLoading && todayBookings.length === 0 ? (
                <Spinner variant="inline" label="جاري تحميل حجوزات اليوم..." />
              ) : todayBookings.length === 0 ? (
                <EmptyState
                  icon="calendar-x"
                  title="لا توجد حجوزات اليوم"
                  message="استمتع بيومك — أو أضف حجزاً يدوياً من صفحة المواعيد."
                />
              ) : (
                <ul className="custom-scrollbar max-h-[340px] space-y-2 overflow-y-auto pl-1">
                  {todayBookings.map((booking) => (
                    <li
                      key={booking.id}
                      className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/5 p-3"
                    >
                      <span className="rounded-lg bg-slate-800 px-2 py-1 font-mono text-xs text-cyan-300">
                        {formatTime12(booking.time)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-white">{booking.patientName}</p>
                        <p className="text-[11px] text-slate-400">
                          {typeLabel(booking.type)}
                          {booking.patientPhone ? ` — ${booking.patientPhone}` : ''}
                        </p>
                      </div>
                      <Badge className={statusStyle(booking.status)}>
                        {statusLabel(booking.status)}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            {/* Monthly comparison */}
            <Panel>
              <div className="mb-4 flex items-center justify-between gap-3">
                <SectionTitle icon="bar-chart-2" className="!mb-0">
                  إحصائيات العيادة
                </SectionTitle>
                <Select
                  value={selectedMonth}
                  onChange={(event) => setSelectedMonth(event.target.value)}
                  className="!w-auto !py-2 text-xs"
                >
                  {MONTH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </div>

              {dashboardState.loading && !dashboard.comparativeStats ? (
                <Spinner variant="inline" />
              ) : !comparison?.current ? (
                <EmptyState icon="bar-chart" title="لا توجد بيانات كافية" />
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <MetricTile
                    label="عدد الحجوزات"
                    value={comparison.current.bookings}
                    delta={comparison.delta?.bookings}
                  />
                  <MetricTile
                    label="كشوفات مكتملة"
                    value={comparison.current.completed}
                    delta={comparison.delta?.completed}
                  />
                  <MetricTile
                    label="الإيرادات"
                    value={formatCurrency(comparison.current.revenue)}
                    delta={comparison.delta?.revenue}
                  />
                  <MetricTile
                    label="أيام العمل"
                    value={comparison.current.activeDays}
                    delta={comparison.delta?.activeDays}
                  />
                </div>
              )}
            </Panel>
          </div>

          {/* Daily performance */}
          <Panel>
            <SectionTitle icon="calendar-range">أداء الأيام — الشهر الحالي</SectionTitle>

            {dashboard.dailyPerformance.length === 0 ? (
              <EmptyState icon="calendar" title="لا توجد بيانات لهذا الشهر" />
            ) : (
              <>
                <div className="grid grid-cols-7 gap-1.5 sm:grid-cols-10 lg:grid-cols-[repeat(16,minmax(0,1fr))]">
                  {dashboard.dailyPerformance.map((day) => (
                    <div
                      key={day.date}
                      title={`${isoToDisplayDate(day.date)} — ${day.totalBookings} حجز — ${formatCurrency(day.dailyRevenue)}`}
                      className={`flex aspect-square flex-col items-center justify-center rounded-lg border text-[10px] ${
                        DAY_STATUS_STYLES[day.status]
                      }`}
                    >
                      <span className="font-bold leading-none">{day.date.slice(-2)}</span>
                      {day.totalBookings > 0 && (
                        <span className="mt-0.5 opacity-70">{day.totalBookings}</span>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  {Object.entries(DAY_STATUS_LABELS).map(([status, label]) => (
                    <span key={status} className="flex items-center gap-1.5 text-[11px] text-slate-400">
                      <span
                        className={`inline-block h-3 w-3 rounded border ${DAY_STATUS_STYLES[status]}`}
                      />
                      {label}
                    </span>
                  ))}
                  <span className="mr-auto text-[11px] text-slate-500">
                    {activeDays.length} يوم به حجوزات
                  </span>
                </div>
              </>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}

function MetricTile({ label, value, delta }) {
  const hasDelta = Number.isFinite(delta);
  const positive = hasDelta && delta > 0;
  const negative = hasDelta && delta < 0;

  return (
    <div className="rounded-xl border border-white/5 bg-white/5 p-3">
      <p className="text-[11px] text-slate-400">{label}</p>
      <p className="mt-1 text-lg font-bold text-white">{value}</p>
      {hasDelta && delta !== 0 && (
        <p
          className={`mt-0.5 flex items-center gap-0.5 text-[11px] font-bold ${
            positive ? 'text-emerald-400' : negative ? 'text-red-400' : 'text-slate-400'
          }`}
        >
          <Icon name={positive ? 'trending-up' : 'trending-down'} size={12} />
          {Math.abs(delta)}%
        </p>
      )}
    </div>
  );
}

export default DoctorOverview;
